// SPDX-License-Identifier: LicenseRef-Proprietary

import { useMemo } from 'react';
import type { VaccineOutputs } from '../lib/models';

/**
 * Hook to calculate fiscal data series from model outputs
 * Extracted from useVaccineDashboard for better separation of concerns
 */
export function useVaccineFiscalData(
  model: VaccineOutputs | null,
  timeHorizon: number
): Array<{ year: number; programmeCost: number; healthcareSavings: number }> {
  return useMemo(() => {
    if (!model) return [];
    
    const years = Math.max(1, timeHorizon);
    const perYearCost = model.vaccineCosts / years / 1_000_000;
    const rawSavings = Array.from({ length: years }, (_, i) => 
      i === 0 ? 0 : (model.series[i - 1]?.savingsEUR || 0)
    );
    const smoothSavings: number[] = [];
    for (let i = 0; i < years; i++) {
      const prev = i === 0 ? 0 : smoothSavings[i - 1];
      smoothSavings[i] = Math.max(prev, rawSavings[i]);
    }
    return Array.from({ length: years }, (_, i) => ({
      year: i,
      programmeCost: perYearCost,
      healthcareSavings: smoothSavings[i] / 1_000_000,
    }));
  }, [timeHorizon, model]);
}

